import L from 'leaflet';
import { campusLocationCoords } from './campusLocations';

// St. George campus, used when a truck has no pin yet.
export const DEFAULT_MAP_CENTER = [43.6629, -79.3957];
export const LOCATION_NOTE_MAX = 120;

export const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export function hasExactLocation(loc) {
  return !!loc && Number.isFinite(Number(loc.lat)) && Number.isFinite(Number(loc.lng));
}

export function directionsUrl(loc) {
  return `https://www.google.com/maps/dir/?api=1&destination=${Number(loc.lat)},${Number(loc.lng)}`;
}

function validCoords(value) {
  if (!value) return null;
  const lat = Number(value.lat);
  const lng = Number(value.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export function getTruckCoordinate(truck) {
  return validCoords(truck?.exact_location) || validCoords(campusLocationCoords(truck?.location));
}

export function haversineDistanceKm(from, to) {
  const start = validCoords(from);
  const end = validCoords(to);
  if (!start || !end) return null;
  const earthRadiusKm = 6371;
  const dLat = ((end.lat - start.lat) * Math.PI) / 180;
  const dLng = ((end.lng - start.lng) * Math.PI) / 180;
  const lat1 = (start.lat * Math.PI) / 180;
  const lat2 = (end.lat * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistance(distanceKm) {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) return null;
  if (distanceKm < 1) return `${Math.max(10, Math.round(distanceKm * 1000 / 10) * 10)} m away`;
  return `${distanceKm.toFixed(distanceKm < 10 ? 1 : 0)} km away`;
}

export function withTruckDistance(truck, customerCoords) {
  const distanceKm = haversineDistanceKm(customerCoords, getTruckCoordinate(truck));
  return { ...truck, distance_km: distanceKm, distance_label: formatDistance(distanceKm) };
}

export function sortTrucksByDistance(trucks, customerCoords) {
  if (!customerCoords) return [...trucks];
  return trucks
    .map((truck, index) => ({ truck: withTruckDistance(truck, customerCoords), index }))
    .sort((a, b) => {
      const aDistance = a.truck.distance_km;
      const bDistance = b.truck.distance_km;
      if (aDistance == null && bDistance == null) return a.index - b.index;
      if (aDistance == null) return 1;
      if (bDistance == null) return -1;
      return aDistance - bDistance || a.index - b.index;
    })
    .map(({ truck }) => truck);
}

// divIcon avoids Leaflet's default marker images, which don't resolve through the bundler.
export const truckPinIcon = L.divIcon({
  className: '',
  html: '<div style="width:22px;height:22px;border-radius:9999px;background:#B00BF0;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
